import React from "react";
import { Checkbox, Button } from "@material-ui/core";
import Business from "@material-ui/icons/Business";
import People from "@material-ui/icons/People";
import AccountBalance from "@material-ui/icons/AccountBalance";
import Description from "@material-ui/icons/Description";
import CheckCircle from "@material-ui/icons/CheckCircle";
import RadioButtonUnchecked from "@material-ui/icons/RadioButtonUnchecked";
import AssignmentOutlined from "@material-ui/icons/AssignmentOutlined";
import ResponsiveSignature from "./ui/ResponsiveSignature";
import LocaleContext from "../../../i18n/LocaleContext";
import Terms from "../Terms";
import TermsAdditional from "../TermsAdditional";
import { EditButton, StepHeader, useTranslation } from "./ui";
import { displayAddress, formatMoney } from "./values";

function CardHeader({ icon: Icon, title, subtitle, children }) {
  const t = useTranslation();
  return (
    <header>
      <span className="merchant-card-icon">
        <Icon aria-hidden="true" />
      </span>
      <div className="merchant-card-title">
        <h2>{t(title)}</h2>
        <p>{t(subtitle)}</p>
      </div>
      {children}
    </header>
  );
}

function Card({ title, subtitle, icon, step, onEdit, rows, children }) {
  return (
    <section className="merchant-review-card">
      <CardHeader icon={icon} title={title} subtitle={subtitle}>
        <EditButton onClick={() => onEdit(step)} icon />
      </CardHeader>
      <ReviewRows rows={rows} />
      {children}
    </section>
  );
}

function ReviewRows({ rows }) {
  const t = useTranslation();
  return (
    <dl className="merchant-review-details">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{t(label)}</dt>
          <dd>{value || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function Agreement({ checked, onChange, link, label, date, error }) {
  const t = useTranslation();
  return (
    <>
      <div
        className={`merchant-agreement${
          checked ? " merchant-agreement-on" : ""
        }`}
      >
        <Checkbox
          color="primary"
          checked={checked}
          onChange={onChange}
          icon={<RadioButtonUnchecked />}
          checkedIcon={<CheckCircle />}
          inputProps={{ "aria-label": t(label) }}
        />
        <div className="merchant-agreement-text">
          <strong>{link}</strong>
          <span>{t(label)}</span>
        </div>
        {checked && (
          <div className="merchant-accepted">
            <strong>{t("Accepted")}</strong>
            <span>{date}</span>
          </div>
        )}
      </div>
      {error && (
        <p role="alert" className="merchant-error">
          {error}
        </p>
      )}
    </>
  );
}

export default class Review extends React.Component {
  static contextType = LocaleContext;
  render() {
    const {
      application: a,
      onEdit,
      onAgreeCheckChange,
      onAgreeAdditionalCheckChange,
      signatureRef,
      onClearSignature,
      onSignatureEnd,
      errors
    } = this.props;
    const t = this.context;
    const today = new Date().toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric"
    });
    return (
      <>
        <StepHeader title="Review Your Application" icon={AssignmentOutlined} align="left">
          Please review all of your information below. If you need to make any
          changes, click the Edit button next to each section. When everything
          looks correct, click Submit Application.
        </StepHeader>
        <Card
          title="Business Information"
          subtitle="Your business details and contact information."
          icon={Business}
          step={1}
          onEdit={onEdit}
          rows={[
            ["Legal Business Name", a.legalBusinessName],
            ["Doing Business As (DBA)", a.doingBusinessAs],
            ["Phone Number", a.businessPhone],
            ["Email Address", a.email],
            ["Business Address", displayAddress(a, "business")],
            [
              "Equipment Shipping Address",
              displayAddress(a, a.shippingDifferent ? "shipping" : "business")
            ],
            ["Business Description", a.businessDescription],
            ["What year did you open your business?", a.businessYear],
            [
              "Estimated Monthly Credit and Debit Card Sales",
              formatMoney(a.cardSales)
            ]
          ]}
        >
          <div className="merchant-review-links">
            <EditButton onClick={() => onEdit(0)} label="Change email" />
            <EditButton
              onClick={() => onEdit(3)}
              label="Edit sales estimates"
            />
          </div>
        </Card>
        <Card
          title="Owner Information"
          subtitle="Primary owner and personal information."
          icon={People}
          step={2}
          onEdit={onEdit}
          rows={[
            ["Name", [a.firstName, a.lastName].filter(Boolean).join(" ")],
            ["Date of Birth", a.dateOfBirth],
            [
              "Social Security Number",
              a.encryptedSocialSecurityNumber
                ? t("SSN saved securely")
                : t("Not provided")
            ],
            ["Mobile Phone Number", a.homePhone],
            [
              "Home Address",
              displayAddress(a, a.residenceDifferent ? "home" : "business")
            ]
          ]}
        />
        <Card
          title="Bank Account Information"
          subtitle="Where you would like to receive your funds."
          icon={AccountBalance}
          step={4}
          onEdit={onEdit}
          rows={[
            ["Bank Name", a.bankName],
            ["Routing Number", a.routingNumber],
            [
              "Bank Account Number",
              a.bankAccountNumber
                ? `•••• ${String(a.bankAccountNumber).slice(-4)}`
                : ""
            ]
          ]}
        />
        <section className="merchant-review-card merchant-agreements">
          <CardHeader
            icon={Description}
            title="Agreements & Signature"
            subtitle="Terms and conditions and your electronic signature."
          />
          <div className="merchant-agreements-body">
            <Agreement
              checked={!!a.agree}
              onChange={onAgreeCheckChange("agree")}
              link={<Terms application={a} />}
              label="I agree to the Terms and Conditions"
              date={today}
              error={errors.agree}
            />
            <Agreement
              checked={!!a.agreeAdditional}
              onChange={onAgreeAdditionalCheckChange("agreeAdditional")}
              link={<TermsAdditional application={a} initialWidth="md" />}
              label="I agree to Equipment Placement Program"
              date={today}
              error={errors.agreeAdditional}
            />
            <h3>{t("Electronic Signature")}</h3>
            <p className="merchant-signature-note">
              {t("Draw your signature with your mouse or finger:")}{" "}
              {t(
                "If you edit your application after signing, please sign again when you return."
              )}
            </p>
            <div className="merchant-signature">
              <ResponsiveSignature
                signatureRef={signatureRef}
                onEnd={onSignatureEnd}
                canvasProps={{ "aria-label": t("Electronic Signature") }}
              />
              <span className="merchant-signature-date" aria-hidden="true">
                {today}
              </span>
            </div>
            <Button variant="outlined" size="small" onClick={onClearSignature}>
              {t("Clear Signature")}
            </Button>
            {errors.signature && (
              <p role="alert" className="merchant-error">
                {errors.signature}
              </p>
            )}
          </div>
        </section>
      </>
    );
  }
}
